import { getEtSessionDateKey } from './sessionBoundary.js'
import { toChartTimestamp } from '../timezone.js'
import { DYNAMIC_CANVAS_TIMEFRAMES, DEFAULT_DYNAMIC_CANVAS_SCHEDULE, normalizeDynamicCanvasSchedule, dynamicCanvasView } from './dynamicCanvas.js'

export const SESSION_OPEN_TIMEFRAMES = Object.freeze(['1min', '2min', '5min'])

const CHART_MARGIN_TIMEFRAMES = Object.freeze(['1min', '2min', '5min', '15min', '30min', '65min', 'daily', 'weekly'])
export const CHART_MARGIN_TIMEFRAME_LABELS = Object.freeze({
  '1min': '1m',
  '2min': '2m',
  '5min': '5m',
  '15min': '15m',
  '30min': '30m',
  '65min': '65m',
  daily: '1D',
  weekly: '1W',
})
export const DEFAULT_CHART_MARGINS_BY_TIMEFRAME = Object.freeze({
  '1min': Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 80, visibleBars: 400, defaultView: 'dynamic', candlesBeforeOpen: 5, dynamicSchedule: DEFAULT_DYNAMIC_CANVAS_SCHEDULE }),
  '2min': Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 80, visibleBars: 400, defaultView: 'dynamic', candlesBeforeOpen: 5, dynamicSchedule: DEFAULT_DYNAMIC_CANVAS_SCHEDULE }),
  '5min': Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 80, visibleBars: 400, defaultView: 'session-open', candlesBeforeOpen: 2 }),
  '15min': Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 90, visibleBars: 300 }),
  '30min': Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 60, visibleBars: 200 }),
  '65min': Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 40, visibleBars: 120 }),
  daily: Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 50, visibleBars: 130 }),
  weekly: Object.freeze({ top: 0.25, bottom: 0.10, rightOffset: 30, visibleBars: 104 }),
})

function clampNumber(value, fallback, min, max) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(max, Math.max(min, numeric))
}

export function normalizeChartMarginSlot(value, timeframe) {
  const fallback = DEFAULT_CHART_MARGINS_BY_TIMEFRAME[timeframe] || DEFAULT_CHART_MARGINS_BY_TIMEFRAME.daily
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : null
  let top = clampNumber(source?.top, fallback.top, 0, 0.8)
  let bottom = clampNumber(source?.bottom, fallback.bottom, 0, 0.8)
  if (top + bottom > 0.9) {
    bottom = Math.max(0, 0.9 - top)
  }
  const visibleBars = Math.round(clampNumber(source?.visibleBars, fallback.visibleBars, 10, 2000))
  const maxRightOffset = Math.min(300, Math.max(0, visibleBars - 1))
  const dynamic = DYNAMIC_CANVAS_TIMEFRAMES.includes(timeframe)
  // One-time in-memory adoption of Brandon's approved 1m/2m defaults. Once the
  // schedule is saved, explicit opt-out and custom pre-open counts survive.
  const migrate = dynamic && !Array.isArray(source?.dynamicSchedule)
  return {
    top,
    bottom,
    rightOffset: Math.round(clampNumber(source?.rightOffset, fallback.rightOffset, 0, maxRightOffset)),
    visibleBars,
    ...(SESSION_OPEN_TIMEFRAMES.includes(timeframe) ? {
      defaultView: migrate ? 'dynamic' : ['latest','session-open',...(dynamic ? ['dynamic'] : [])].includes(source?.defaultView) ? source.defaultView : fallback.defaultView,
      candlesBeforeOpen: migrate ? 5 : Math.round(clampNumber(source?.candlesBeforeOpen, fallback.candlesBeforeOpen, 0, 100)),
      ...(dynamic ? { dynamicSchedule: normalizeDynamicCanvasSchedule(source?.dynamicSchedule) } : {}),
    } : {}),
  }
}

export function normalizeChartMarginsByTimeframe(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : null
  return Object.fromEntries(
    CHART_MARGIN_TIMEFRAMES.map((timeframe) => [
      timeframe,
      normalizeChartMarginSlot(source?.[timeframe], timeframe),
    ])
  )
}

export function chartMarginsForTimeframe(settings, timeframe) {
  const normalized = normalizeChartMarginsByTimeframe(settings?.marginsByTimeframe)
  return normalized[timeframe] || normalized.daily
}

export function applyDefaultVisibleLogicalRange(chart, totalBars, visibleBars, rightOffset, mode = 'live', lastCandleTime = null, openingView = {}) {
  const candleIndex = lastCandleTime == null ? null : chart.timeScale().timeToIndex?.(lastCandleTime)
  // Pane series share one time axis; their earlier dates can shift candle indices.
  const count = Number.isFinite(candleIndex) ? candleIndex + 1 : Number(totalBars)
  if (!Number.isFinite(count) || count <= 0) return false
  const bars = Math.round(clampNumber(visibleBars, count, 10, 2000))
  const offset = Math.round(clampNumber(rightOffset, 0, 0, Math.min(300, Math.max(0, bars - 1))))
  try {
    if (mode === 'live' && openingView.defaultView === 'dynamic') {
      const view = dynamicCanvasView({ ...openingView, schedule: openingView.dynamicSchedule,
        timeToIndex: time => chart.timeScale().timeToIndex?.(time) })
      if (view) {
        chart.timeScale().applyOptions?.({ shiftVisibleRangeOnNewBar: false })
        chart.timeScale().setVisibleLogicalRange(view.range)
        return true
      }
    }
    if (mode !== 'live' && count <= bars) {
      chart.timeScale().fitContent()
      return true
    }
    let range = { from: Math.max(0, count - bars), to: count + offset }
    const { candles, timeframe, defaultView, candlesBeforeOpen } = openingView
    if (mode === 'live' && defaultView === 'session-open' && SESSION_OPEN_TIMEFRAMES.includes(timeframe) && candles?.length) {
      // Session identity uses real instants, never display-time chart timestamps.
      const dateKey = getEtSessionDateKey(candles.at(-1)?.rawEpochMs)
      const openMs = dateKey
        ? toChartTimestamp(`${dateKey.replaceAll('-', '')} 09:30:00 US/Eastern`, 'UTC') * 1000
        : null
      const openIndex = openMs == null ? -1 : candles.findIndex(bar => bar.rawEpochMs === openMs)
      if (openIndex >= 0) {
        const nativeOpenIndex = chart.timeScale().timeToIndex?.(candles[openIndex].time)
        const nativeLastIndex = chart.timeScale().timeToIndex?.(candles.at(-1).time)
        const openingIndex = Number.isFinite(nativeOpenIndex) ? nativeOpenIndex : openIndex
        const lastIndex = Number.isFinite(nativeLastIndex) ? nativeLastIndex : candles.length - 1
        const from = openingIndex - Math.round(clampNumber(candlesBeforeOpen, 2, 0, 100))
        // Visible bars sets the minimum width. Widen later defaults to retain
        // the opening candle and the latest candle together throughout the day.
        range = { from, to: Math.max(from + bars + offset, lastIndex + 1 + offset) }
      }
    }
    chart.timeScale().setVisibleLogicalRange(range)
    return true
  } catch {
    return false
  }
}
